import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertTriangle, Clock, CheckCheck, ShieldOff, Zap, ArrowDownRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function fmtDuration(iso, endIso) {
  if (!iso) return "—";
  const start = new Date(iso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const s = Math.max(0, Math.floor((end - start) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export default function KpiViolationsPanel() {
  const { user } = useAuth();
  const [violations, setViolations] = useState([]);
  const [monitor, setMonitor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");

  const load = async () => {
    try {
      const [v, m] = await Promise.all([
        api.get(`/kpi-violations?status=${tab}`),
        api.get("/kpi-monitor/status"),
      ]);
      setViolations(v.data);
      setMonitor(m.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, [tab]);

  const canAck = user?.role === "manager" || user?.role === "admin";

  const acknowledge = async (id) => {
    try {
      await api.post(`/kpi-violations/${id}/acknowledge`);
      toast.success("Violation acknowledged");
      load();
    } catch {
      toast.error("Could not acknowledge");
    }
  };

  const runCheckNow = async () => {
    try {
      const { data } = await api.post("/kpi-monitor/check-now");
      toast.success(`Check complete · opened ${data.opened} · resolved ${data.resolved}`);
      load();
    } catch {
      toast.error("Check failed");
    }
  };

  const openCount = violations.filter((v) => v.status === "open").length;
  const sustainedCount = violations.filter((v) => v.sustained).length;

  return (
    <div className="border border-gray-200 bg-white rounded-sm p-6" data-testid="kpi-violations-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-gray-500" strokeWidth={2} />
          <span className="overline text-gray-400">KPI Violations · Rules Engine</span>
        </div>
        <div className="flex items-center gap-2">
          {monitor?.running && (
            <span className="text-[10px] font-mono text-green-700 flex items-center gap-1" data-testid="monitor-status">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
              MONITOR LIVE · sustained ≥ {monitor.sustained_minutes}m
            </span>
          )}
          {canCheck() && (
            <Button
              onClick={runCheckNow}
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs rounded-sm"
              data-testid="check-now-btn"
            >
              <Zap className="w-3 h-3 mr-1" /> Check now
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-1 mb-5 leading-relaxed">
        Automatic monitor runs every {monitor?.tick_seconds ?? 60}s. Metrics above target for &gt;{monitor?.sustained_minutes ?? 15} min flip to <strong className="text-[#DC2626]">sustained</strong>.
      </p>

      <div className="flex items-center gap-1 mb-4 border-b border-gray-100">
        {["open", "resolved"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-violations-${t}`}
            className={`px-3 py-1.5 text-xs overline transition-colors ${
              tab === t
                ? "border-b-2 border-black -mb-px text-black"
                : "text-gray-400 hover:text-black"
            }`}
          >
            {t} {t === "open" && openCount > 0 ? `(${openCount})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 py-4">Loading violations…</div>
      ) : violations.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-sm p-8 text-center text-sm text-gray-500">
          <CheckCheck className="w-5 h-5 mx-auto mb-2 text-[#16A34A]" />
          {tab === "open" ? "All KPIs within target." : "No resolved violations yet."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="violations-list">
          {violations.map((v) => (
            <div
              key={v.id}
              className={`border rounded-sm p-4 ${
                v.sustained ? "border-[#DC2626] bg-red-50/40" :
                v.status === "resolved" ? "border-gray-200 opacity-70" : "border-orange-300 bg-orange-50/40"
              }`}
              data-testid={`violation-${v.kpi_key}`}
            >
              <div className="flex items-start justify-between gap-4 mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`risk-pill ${v.sustained ? "high" : v.status === "resolved" ? "low" : "medium"}`}>
                      {v.sustained ? "SUSTAINED" : v.status === "resolved" ? "RESOLVED" : "OPEN"}
                    </span>
                    <span className="font-medium text-sm">{v.kpi_label}</span>
                    {v.acknowledged && (
                      <span className="text-[10px] font-mono text-green-700 bg-green-50 border border-green-200 rounded-sm px-1.5 py-0.5">
                        ACK
                      </span>
                    )}
                  </div>
                  <div className="text-xs font-mono text-gray-600 flex items-center gap-3 flex-wrap">
                    <span>target {v.op === "lte" ? "≤" : "≥"} {v.target}{v.unit}</span>
                    <span className="text-[#DC2626]">last {v.last_measured}{v.unit}</span>
                    <span className="text-gray-400">peak {v.peak_measured}{v.unit}</span>
                  </div>
                </div>
                <div className="text-right text-xs">
                  <div className="flex items-center gap-1 text-gray-500">
                    <Clock className="w-3 h-3" />
                    <span className="font-mono">{fmtDuration(v.first_seen_at, v.resolved_at)}</span>
                  </div>
                  <div className="text-gray-400 text-[10px] font-mono mt-1">
                    {fmtTime(v.first_seen_at)} → {v.resolved_at ? fmtTime(v.resolved_at) : "now"}
                  </div>
                </div>
              </div>

              {!v.sustained && v.status === "open" && (
                <div className="text-[10px] font-mono text-gray-400 mt-2">
                  <ArrowDownRight className="w-2.5 h-2.5 inline mr-1" />
                  will flip to SUSTAINED at {fmtTime(new Date(new Date(v.first_seen_at).getTime() + (monitor?.sustained_minutes ?? 15) * 60000).toISOString())}
                </div>
              )}

              {v.status === "open" && !v.acknowledged && canAck && (
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => acknowledge(v.id)}
                    data-testid={`ack-${v.kpi_key}`}
                    className="rounded-sm h-7 text-xs gap-1"
                  >
                    <ShieldOff className="w-3 h-3" /> Acknowledge
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-gray-100 grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="overline text-gray-400 text-[10px]">open</div>
          <div className="font-mono text-sm">{tab === "open" ? violations.length : "—"}</div>
        </div>
        <div>
          <div className="overline text-gray-400 text-[10px]">sustained</div>
          <div className="font-mono text-sm text-[#DC2626]">{sustainedCount}</div>
        </div>
        <div>
          <div className="overline text-gray-400 text-[10px]">last check</div>
          <div className="font-mono text-sm">{monitor?.last_tick_at ? fmtTime(monitor.last_tick_at) : "—"}</div>
        </div>
      </div>
    </div>
  );

  function canCheck() {
    return user?.role === "manager" || user?.role === "admin";
  }
}
